import { KeyValue } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { BaseComponent } from '../base-component';
import { AFDirectorshipsDomain } from '../model/af.model';
import { AuthService } from '../service/auth.service';
import { CustomValidatorsService } from '../service/custom-validators.service';
import { DirectorshipService } from '../service/directorship.service';
import { EventService } from '../service/event.service';
import { BeforeSubmitErrorEvent, ChangeNavigationEvent, ProgressBarEvent, ShowErrorEvent, ShowProgressEvent, SkipToMainContentEvent, ValidateAndNavigateEvent } from '../service/events';


@Component({
  selector: 'app-directorship',
  templateUrl: './directorship.component.html',
  styleUrls: ['./directorship.component.scss']
})
export class DirectorshipComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

  directorshipForm: UntypedFormGroup;
  maxDate = new Date();
  minDate = new Date(1900, 0, 1);
  directorshipData: AFDirectorshipsDomain;
  @ViewChild("addElement") addElement: ElementRef;
  @ViewChild("addButtonElement") addButtonElement: ElementRef;
  date: any;
  check: boolean = false;
  blueBorder: boolean = false;
  redBorder: boolean = false;
  checkRadio: boolean = false;
  hasBeenSubmittted: boolean = false;
  border: boolean = false;
  jobCountry: string = '';

  constructor(private userService: AuthService, private eventBus: EventService, private directorshipService: DirectorshipService, private fb: UntypedFormBuilder,
    private _router: Router, private _snackBar: MatSnackBar, private customValidator: CustomValidatorsService) {
    super(eventBus, _router);
  }

  ngOnInit(): void {
    this.subscribeEvents();
    this.createGroup();
    this.jobCountry = this.userService.getJobCountry();
  }

  get afDirectorshipsList() { return this.directorshipForm.get('afDirectorshipsList') as UntypedFormArray; }
  get director() { return this.directorshipForm.get('director') }
  get compensated() { return this.directorshipForm.get('compensated') }
  get submit() { return this.directorshipForm.get('submit') }
  get bookmarkUrl() { return this.directorshipForm.get('bookmarkUrl') }

  subscribeEvents() {
    this.directorshipService.getDirectorShip().subscribe({
      next: (data: AFDirectorshipsDomain) => {
        this.directorshipData = data;
        this.populateDirectorships(this.directorshipData);
        if (this.hasDirectorships()) {
          if (this.afDirectorshipsList.length === 0) {
            this.createContribution("");
          }
        }
      },
      error: (error: Error) => {
        console.log(error);
      }
    });

    this.sub = this.eventBus.on(
      ValidateAndNavigateEvent,
      (event): void => {
        if (event.payload.currentLink === '/ous-directorship') {
          this.submit.setValue(false);
          this.bookmarkUrl.setValue("");
          if (event.payload.save) {
            this.submitDirectorship((this.directorshipForm.value), event);
          } else if (event.payload.saveAndFinishLater) {
            this.bookmarkUrl.setValue(event.payload.currentLink);
            this.submitDirectorship((this.directorshipForm.value), event);
          } else {
            let hasDirectorShips: boolean = this.hasDirectorships();
            if (hasDirectorShips && this.afDirectorshipsList.length == 0) {
              this.createDirector('');
              this.focusTarget();
              this.eventBus.emit(new ShowProgressEvent({ show: false }));
              this.eventBus.emit(new ShowErrorEvent({ httpStatus: 500, text: 'At least one Directorship is Required.' }));
            } else {
              if (this.validate()) {
                this.submitDirectorship((this.directorshipForm.value), event);
              } else {
                this.eventBus.emit(new BeforeSubmitErrorEvent({}));
              }
            }
          }
        }
      });
  }

  focusTarget() {
    setTimeout(() => {
      this.addElement.nativeElement.focus();
      this.border = true;
    });
  }

  validate() {
    this.submit.setValue(true);
    this.hasBeenSubmittted = true;
    if (!this.hasDirectorships()) {
      this.afDirectorshipsList.clear();
    }

    this.directorshipForm.markAllAsTouched();
    return !this.directorshipForm.invalid;
  }

  submitDirectorship(directorships, event) {
    this.directorshipService.submitDirectorships(directorships).subscribe({
      next: () => {
        if (event.payload.save) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
        } else if (event.payload.saveAndFinishLater) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
          this._router.navigate(['sessionTimedOut']);
        } else {
          this.eventBus.emit(new ChangeNavigationEvent({ id: event.payload.id }));
          this._router.navigateByUrl(event.payload.nextNavLink);
        }
      },
      error: (error: any) => console.log(error)
    });
  }

  valueAscOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
    return a.value.localeCompare(b.value);
  }

  populateDirectorships(directorshipData: AFDirectorshipsDomain) {
    this.directorshipForm.patchValue(directorshipData);
    for (let directorship of this.directorshipData.afDirectorshipsList) {
      let fg: UntypedFormGroup = this.fieldsGroup();
      fg.patchValue(directorship);
      this.afDirectorshipsList.push(fg);
    }
  }

  ngAfterViewInit() {
    this.directorshipForm.valueChanges.subscribe(() => {
      setTimeout(() => {
        let progress = this.getPercentage(this.directorshipForm);
        this.eventBus.emit(new ProgressBarEvent({ id: progress }));
      }, 100);
    });

    // retain navigation on page refresh
    let navlink = this.userService.getNavLinks().find(e => e.routerLink === "/ous-directorship");
    this.eventBus.emit(new ChangeNavigationEvent({ id: navlink.id }));
  }

  getPercentage(directorshipForm: UntypedFormGroup): number {
    let errorCount = 0;
    let totalCount = 0;
    let container: UntypedFormGroup = directorshipForm;

    for (let controlKey in container.controls) {

      if (container.controls.hasOwnProperty(controlKey)) {
        if (container.controls[controlKey].validator != null) {
          totalCount += container.controls[controlKey].validator.length;
        }
        if (container.controls[controlKey].errors) {
          errorCount += Object.keys(container.controls[controlKey].errors).length;
        }
      }
    }

    if (this.hasDirectorships()) {
      for (let container of directorshipForm.get('afDirectorshipsList')['controls']) {
        for (let controlKey in container.controls) {

          if (container.controls.hasOwnProperty(controlKey)) {
            if (container.controls[controlKey].validator != null) {
              totalCount += container.controls[controlKey].validator.length;
            }
            if (container.controls[controlKey].errors) {
              errorCount += Object.keys(container.controls[controlKey].errors).length;
            }
          }
        }
      }
    }

    let percentage = ((totalCount - errorCount) / totalCount) * 100;
    return percentage;
  }

  hasDirectorships(): boolean {
    let hasDirectorShips: boolean = (this.compensated.value == 1 || this.director.value == 1);
    return (hasDirectorShips);
  }

  createGroup() {
    this.directorshipForm = this.fb.group({
      submit: [false],
      verifyId: [this.userService.getVerifyId(), []],
      director: ['', [Validators.required]],
      compensated: ['', [Validators.required]],
      bookmarkUrl: [''],
      afDirectorshipsList: new UntypedFormArray([])
    });
  }

  createDirector(check: string) {
    let fg: UntypedFormGroup = this.fieldsGroup();
    this.afDirectorshipsList.push(fg);
    setTimeout(() => {
      if (check === 'click') {
        this.addElement.nativeElement.focus();
      }
      this.blueBorder = true;
    }, 0);
  }

  deleteClick(i) {
    this.afDirectorshipsList.removeAt(i);

    setTimeout(() => {
      if (this.afDirectorshipsList && this.afDirectorshipsList.length == 0) {
        this.addButtonElement.nativeElement.focus();
        this.blueBorder = false;
      } else {
        this.addElement.nativeElement.focus();
        this.blueBorder = true;
      }
    }, 0);
  }

  radioOnBlur() {
    if (this.director.value) {
      this.checkRadio = false;
    } else if (this.director.value === 0) {
      this.checkRadio = false;
    } else {
      this.checkRadio = true;
    }
  }

  onDirectorshipChange() {
    if (this.hasDirectorships()) {
      if (this.afDirectorshipsList.length == 0) {
        this.createContribution("");
      }
    }
  }

  radioOnFocus() {
    this.checkRadio = false;
  }

  createContribution(check: string) {
    let fg: UntypedFormGroup = this.fieldsGroup();
    this.afDirectorshipsList.push(fg);
    setTimeout(() => {
      if (check == 'click') {
        this.addElement.nativeElement.focus();
      }
      this.blueBorder = true;
    }, 0);
  }

  onFocusOutMainContent() {
    this.eventBus.emit(new SkipToMainContentEvent({ text: "FocusOff" }));
  }

  fieldsGroup(): UntypedFormGroup {
    return this.fb.group({
      companyName: ['', [Validators.required, Validators.maxLength(100), this.customValidator.validateRequiredField]],
      startDate: ['', [Validators.required, Validators.maxLength(100)]],
      endDate: ['', [Validators.required, Validators.maxLength(100)]],
      status: ['', [Validators.required]],
      comments: ['', [Validators.required, Validators.maxLength(4000), this.customValidator.validateRequiredField]],
      lastAccountFixedDate: ['', [Validators.required, Validators.maxLength(100)]],
      vatRegNumber: ['', [Validators.required, Validators.maxLength(100), this.customValidator.validateRequiredField]]
    })
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}